import React, { useEffect, useRef, useState } from "react";
import { FiArrowUp } from "react-icons/fi";
import "../style/projectSnapCarousel.css";

function ProjectSnapCarousel({
  items = [],
  renderItem,
  ariaLabel = "Projects",
  className = "",
  footer = null,
  showBackToTop = false,
}) {
  const carouselRef = useRef(null);
  const itemRefs = useRef([]);
  const frameRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const carousel = carouselRef.current;

    if (!carousel) {
      return undefined;
    }

    const updateActiveProject = () => {
      const carouselCenter =
        carousel.getBoundingClientRect().top + carousel.clientHeight / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      itemRefs.current.forEach((item, index) => {
        if (!item) {
          return;
        }

        const itemRect = item.getBoundingClientRect();
        const itemCenter = itemRect.top + itemRect.height / 2;
        const distance = Math.abs(carouselCenter - itemCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      setActiveIndex(closestIndex);
      frameRef.current = null;
    };

    const handleScroll = () => {
      if (frameRef.current === null) {
        frameRef.current = window.requestAnimationFrame(updateActiveProject);
      }
    };

    updateActiveProject();
    carousel.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      carousel.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);

      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, [items.length]);

  const scrollToTop = () => {
    carouselRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <div
        className={`project-snap-carousel ${className}`}
        ref={carouselRef}
        aria-label={ariaLabel}
        tabIndex="0"
      >
        {items.map((item, index) => (
          <div
            key={item.title ?? index}
            ref={(element) => {
              itemRefs.current[index] = element;
            }}
            className={`project-snap-item ${
              index === activeIndex ? "active" : ""
            }`}
          >
            {renderItem(item, index, index === activeIndex)}
          </div>
        ))}
        {footer && <div className="project-snap-footer">{footer}</div>}
      </div>

      {showBackToTop && (
        <button
          type="button"
          className="project-back-to-top"
          aria-label="Go to the top of the page"
          onClick={scrollToTop}
        >
          <FiArrowUp aria-hidden="true" />
        </button>
      )}
    </>
  );
}

export default ProjectSnapCarousel;
